// src/pages/ConsultarDocentes.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/formularios.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.png';
import { resolveApiBaseList } from '../utils/apiBase';

// Parse a date string like 'YYYY-MM-DD' as local date (avoids UTC parse off-by-one)
function parseIsoDateToLocal(dateStr) {
  if (!dateStr) return null;
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    return new Date(y, mo, d);
  }
  const parsed = new Date(dateStr);
  if (isNaN(parsed)) return null;
  return parsed;
}

function traducirSexo(valor) {
  if (!valor) return 'N/A';
  const v = String(valor).toUpperCase();
  if (v === 'M') return 'Hombre';
  if (v === 'F') return 'Mujer';
  return 'Otro';
}

const ConsultarDocentes = () => {
  // UI states
  const [mostrarBusquedaNombre, setMostrarBusquedaNombre] = useState(false);
  const [mostrarCumples, setMostrarCumples] = useState(false);

  // Combo de nombres
  const [nombres, setNombres] = useState([]); // [{NombreCompleto}]
  const [nombreSel, setNombreSel] = useState('');

  // Docente seleccionado
  const [docente, setDocente] = useState(null);
  const [loadingDocente, setLoadingDocente] = useState(false);

  // Cumpleañeros del mes
  const [cumplesMes, setCumplesMes] = useState([]);
  const [loadingCumples, setLoadingCumples] = useState(false);

  const apiBases = useMemo(() => resolveApiBaseList().map((base) => `${base}/docentes`), []);

  const fetchJsonWithFallback = useCallback(async (suffix, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const url = `${base}${suffix}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // Keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return trimmed ? JSON.parse(trimmed) : {};
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  // ---- cargar nombres (equivale a cargarNombresDocentes) ----
  const cargarNombres = useCallback(async () => {
    try {
      const data = await fetchJsonWithFallback('/nombres');
      setNombres(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error al cargar nombres de docentes:', err);
      setNombres([]);
    }
  }, [fetchJsonWithFallback]);

  // Mostrar sección de búsqueda por nombre
  const irABusquedaPorNombre = async () => {
    setMostrarBusquedaNombre(true);
    setMostrarCumples(false);
    setDocente(null);
    setNombreSel('');
    if (nombres.length === 0) await cargarNombres();
  };

  // Mostrar sección de cumpleaños del mes
  const irACumplesMes = async () => {
    setMostrarBusquedaNombre(false);
    setDocente(null);
    setNombreSel('');
    await consultarCumpleaniosDocente();
    setMostrarCumples(true);
  };

  // ---- seleccionar nombre -> obtener ficha (equivale a mostrarInfoDocente) ----
  const handleSeleccion = async (e) => {
    const nombre = e.target.value;
    setNombreSel(nombre);
    if (!nombre) return;

    setLoadingDocente(true);
    try {
      const d = await fetchJsonWithFallback(`/por-nombre/${encodeURIComponent(nombre)}`);

      if (!d) {
        setDocente(null);
        return;
      }

      setDocente({
        nombreCompleto: `${d.Nombre ?? ''} ${d.ApellidoPaterno ?? ''} ${d.ApellidoMaterno ?? ''}`.trim(),
        sexo: d.Sexo ?? '',
        correo: d.Correo || 'N/A',
        tipoSangre: d.TipoSangre || 'N/A',
        telefono: d.Telefono || 'N/A',
        puesto: d.Puesto || 'N/A',
      });
    } catch (err) {
      console.error('❌ Error al mostrar información del docente:', err);
      setDocente(null);
    } finally {
      setLoadingDocente(false);
    }
  };

  // ---- cumpleañeros del mes (equivale a consultarCumpleaniosDocente) ----
  const consultarCumpleaniosDocente = async () => {
    setLoadingCumples(true);
    try {
      const data = await fetchJsonWithFallback('/cumpleanios');
      const lista = (Array.isArray(data) ? data : []).map((d) => {
        const fecha = d.FechaNacimiento ? parseIsoDateToLocal(d.FechaNacimiento) : null;
        const fechaFormateada = fecha
          ? fecha.toLocaleDateString('es-MX', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })
          : 'N/A';
        return {
          nombreCompleto: `${d.Nombre ?? ''} ${d.ApellidoPaterno ?? ''} ${d.ApellidoMaterno ?? ''}`.trim(),
          fecha: fechaFormateada,
        };
      });
      setCumplesMes(lista);
    } catch (err) {
      console.error('❌ Error al obtener cumpleaños de docentes:', err);
      setCumplesMes([]);
    } finally {
      setLoadingCumples(false);
    }
  };

  // ---- exportar a Excel (equivale a exportarExcel) ----
  const exportarExcel = () => {
    if (!cumplesMes || cumplesMes.length === 0) return;

    const wb = XLSX.utils.book_new();
    const wsData = [['Nombre', 'Fecha de nacimiento']];
    cumplesMes.forEach((r) => wsData.push([r.nombreCompleto, r.fecha]));

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Cumpleaños');
    XLSX.writeFile(wb, 'cumpleanios_docentes.xlsx');
  };

  // ---- exportar a PDF (equivale a exportarPDF) ----
    const exportarPDF = () => {
    const doc = new jsPDF();
    // Opción 1: leer directo de la tabla HTML
    autoTable(doc, { html: '#tablaCumples', styles: { halign: 'center' }, theme: 'grid' });
    // Opción 2 (si prefieres desde datos):
    // const rows = cumplesMes.map(r => [r.nombreCompleto, r.fecha]);
    // autoTable(doc, { head: [['Nombre','Fecha de nacimiento']], body: rows, styles:{halign:'center'}, theme:'grid' });
    doc.save('cumpleanios_docentes.pdf');
  };

  // (Opcional) cargar nombres al montar por primera vez
  useEffect(() => {
    cargarNombres().catch(() => {});
  }, [cargarNombres]);

  const sexoTraducido = useMemo(() => traducirSexo(docente?.sexo), [docente]);

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="text-center flex-grow-1">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Consultar Docentes</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Botones de búsqueda */}
      <div className="mb-3 d-flex justify-content-center gap-3">
        <button className="btn btn-outline-primary" onClick={irABusquedaPorNombre}>
          Consultar por Nombre
        </button>
        <button className="btn btn-outline-success" onClick={irACumplesMes}>
          Cumpleaños del Mes
        </button>
      </div>

      {/* Búsqueda por nombre */}
      {mostrarBusquedaNombre && (
        <div className="mb-4">
          <label htmlFor="selectNombre" className="form-label">Selecciona el docente:</label>
          <select
            id="selectNombre"
            className="form-select"
            value={nombreSel}
            onChange={handleSeleccion}
          >
            <option value="" disabled>Seleccionar...</option>
            {nombres.map((d, idx) => (
              <option key={idx} value={d.NombreCompleto}>
                {d.NombreCompleto}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Resultado del docente */}
      {docente && (
        <div className="bg-light p-4 rounded shadow-sm">
          <h6 className="mb-3 fw-semibold">Información del Docente</h6>
          <ul className="list-group">
            <li className="list-group-item"><strong>Nombre:</strong> {docente.nombreCompleto}</li>
            <li className="list-group-item"><strong>Sexo:</strong> {sexoTraducido}</li>
            <li className="list-group-item"><strong>Correo:</strong> {docente.correo}</li>
            <li className="list-group-item"><strong>Tipo de Sangre:</strong> {docente.tipoSangre}</li>
            <li className="list-group-item"><strong>Teléfono:</strong> {docente.telefono}</li>
            <li className="list-group-item"><strong>Puesto:</strong> {docente.puesto}</li>
          </ul>
        </div>
      )}

      {/* Cumpleaños del mes */}
      {mostrarCumples && (
        <div className="bg-light p-4 rounded shadow-sm mt-4">
          <h6 className="mb-3 fw-semibold">Docentes que cumplen años este mes</h6>

          {loadingCumples && <p className="text-muted">Cargando…</p>}

          {!loadingCumples && (
            <>
              {cumplesMes.length > 0 ? (
                <>
                  <table className="table table-sm" id="tablaCumples">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Nombre</th>
                        <th>Fecha de Nacimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cumplesMes.map((r, i) => (
                        <tr key={`${r.nombreCompleto}-${i}`}>
                          <td>{i + 1}</td>
                          <td>{r.nombreCompleto}</td>
                          <td>{r.fecha}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="mt-3 d-flex justify-content-end gap-2">
                    <button className="btn btn-outline-success" onClick={exportarExcel}>
                      Exportar a Excel
                    </button>
                    <button className="btn btn-outline-danger" onClick={exportarPDF}>
                      Exportar a PDF
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-muted">No hay docentes que cumplan años este mes.</p>
              )}
            </>
          )}
        </div>
      )}

      {loadingDocente && (
        <div className="mt-3 text-center text-muted">Cargando información del docente…</div>
      )}
    </div>
  );
}


export default ConsultarDocentes;
